import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { useClientTranslations } from "../../../lib/i18n/client";
import styles from "./AddEventChooser.module.css";

/**
 * Phase 37 — Chooser card identity. Decoupled from `EventType` because
 * "appointmentTest" merges two types into one card (figma 2556:79180), and
 * "other" is a visual variant that data-routes to admin.
 */
export type ChooserCardId =
	| "cycle"
	| "medication"
	| "appointmentTest"
	| "admin"
	| "other";

type VisualCategory = "cycle" | "meds" | "test-appt" | "admin" | "other";

interface CardConfig {
	id: ChooserCardId;
	visualCategory: VisualCategory;
}

const CHOOSER_CARDS: readonly CardConfig[] = [
	{ id: "cycle", visualCategory: "cycle" },
	{ id: "medication", visualCategory: "meds" },
	{ id: "appointmentTest", visualCategory: "test-appt" },
	{ id: "admin", visualCategory: "admin" },
	{ id: "other", visualCategory: "other" },
];

interface AddEventChooserProps {
	onSelectCard: (id: ChooserCardId) => void;
}

/**
 * Phase 37 — 5 chooser cards (figma 2556:79180):
 *   Cycle (pink) / Meds (orange) / Test+Appointment (purple) / Administrative (yellow) / Other (white).
 *
 * Visual: each card renders a colored circle with a centred 8px white inner
 * dot — NOT a heroicon. Icons remain everywhere else (calendar dots,
 * EventDetailSheet, etc.); the chooser is the only surface using the dot motif.
 *
 * Tap → onSelectCard(id) → AddEventSheet transitions step='chooser' → 'form'.
 * RTL: ChevronRight flips via [dir="rtl"] in the CSS module.
 *
 * i18n keys: event.add.chooser.cards.{cycle|medication|appointmentTest|admin|other}.{title|subtitle}
 */
export function AddEventChooser({ onSelectCard }: AddEventChooserProps) {
	const { t } = useClientTranslations("track");
	return (
		<div className={styles.list}>
			{CHOOSER_CARDS.map(({ id, visualCategory }) => {
				const isOther = visualCategory === "other";
				return (
					<button
						key={id}
						type="button"
						className={`${styles.card} ${styles[`card--${visualCategory}`]}`}
						onClick={() => onSelectCard(id)}
						aria-label={t(`event.add.chooser.cards.${id}.title`)}
					>
						<span
							className={`${styles.iconDot} ${styles[`iconDot--${visualCategory}`]}`}
							aria-hidden="true"
						>
							<span className={styles.iconDotInner} />
						</span>
						<div className={styles.body}>
							<h3 className={styles.title}>
								{t(`event.add.chooser.cards.${id}.title`)}
							</h3>
							<p className={styles.subtitle}>
								{t(`event.add.chooser.cards.${id}.subtitle`)}
							</p>
						</div>
						<span
							className={`${styles.chevronCircle} ${
								isOther ? styles.chevronCircleOther : ""
							}`}
							aria-hidden="true"
						>
							<ChevronRightIcon className={styles.chevron} aria-hidden="true" />
						</span>
					</button>
				);
			})}
		</div>
	);
}
